import React, { useMemo, useState } from "react";
import { useRecordContext, useRefresh } from "react-admin";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

//Events
import EventBus from "../../EventBus";

//Enums
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";

//Services
import ComboService from "../../services/ComboService";

//Components
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

//Common Styles
import {
  CustomTableRowTR,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";

//Types
interface CouponExtensionRequest {
  id: number;
  couponOrderId: number;
  createdBy?: number | string | null;
  createdByType?: string;
  extendByDays?: number;
  amount?: string;
  status?: string;
  razorpayOrderId?: number | null;
  razorpayOrder?: any;
  createdAt?: string;
  updatedAt?: string;
}

const comboService = new ComboService();

interface ComboCouponExtensionsTabProps {
  extensionRequests?: CouponExtensionRequest[];
  isLoading?: boolean;
  onRefreshData?: () => void;
  title?: string;
}

const formatNullableValue = (value: any) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getCreatedByHref = (
  createdBy: number | string,
  createdByType?: string,
) => {
  const normalizedType = (createdByType || "").toUpperCase();
  const resource = normalizedType === "CUSTOMER" ? "Customers" : "AdminUsers";
  return `/#/${resource}/${createdBy}/show`;
};

const getCouponOrderHref = (couponOrderId: number | string) => {
  return `/#/Combo/${couponOrderId}/show`;
};

const getStatusChipStyles = (status?: string) => {
  switch ((status || "").toUpperCase()) {
    case "PAID":
      return {
        color: "#047857",
        background: "rgba(16, 185, 129, 0.16)",
      };
    case "NOT_PAID":
      return {
        color: "#B45309",
        background: "rgba(245, 158, 11, 0.18)",
      };
    default:
      return {
        color: "#6B7280",
        background: "rgba(107, 114, 128, 0.16)",
      };
  }
};

const ComboCouponExtensionsTab = ({
  extensionRequests: extensionRequestsProp,
  isLoading = false,
  onRefreshData,
  title = "Coupon Extensions",
}: ComboCouponExtensionsTabProps) => {
  const record = useRecordContext<any>();
  const refresh = useRefresh();
  const [submittingRequestId, setSubmittingRequestId] = useState<number | null>(
    null,
  );
  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);
  const [openLinksDialog, setOpenLinksDialog] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string>("");

  const extensionRequests = useMemo<CouponExtensionRequest[]>(() => {
    if (Array.isArray(extensionRequestsProp)) return extensionRequestsProp;
    const requests = record?.expiryExtensionRequests;
    return Array.isArray(requests) ? requests : [];
  }, [extensionRequestsProp, record]);

  const extractLinks = (payload: any): string[] => {
    if (!payload) return [];
    if (typeof payload === "string") return payload ? [payload] : [];
    if (Array.isArray(payload)) {
      return payload.filter((item) => typeof item === "string");
    }

    const knownKeys = [
      "paymentLink",
      "paymentLinks",
      "link",
      "links",
      "url",
      "shortUrl",
    ];

    for (const key of knownKeys) {
      const value = payload?.[key];
      if (typeof value === "string" && value) return [value];
      if (Array.isArray(value)) {
        const links = value.filter((item) => typeof item === "string");
        if (links.length) return links;
      }
    }

    return [];
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(text);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Payment link copied",
        type: CustomSnackbarTypes.SUCCESS,
      });
      setTimeout(() => {
        setCopiedLink("");
      }, 1500);
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to copy payment link",
        type: CustomSnackbarTypes.ERROR,
      });
    }
  };

  const handleSendPaymentLink = async (
    extensionRequestId: number,
    status?: string,
  ) => {
    if (!extensionRequestId) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Extension request id is missing",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    if ((status || "").toUpperCase() === "PAID") {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Payment is already completed for this extension",
        type: CustomSnackbarTypes.INFO,
      });
      return;
    }

    try {
      setSubmittingRequestId(extensionRequestId);
      const response =
        await comboService.sendExtensionPaymentLink(extensionRequestId);
      const links = extractLinks(response);

      if (links.length) {
        setGeneratedLinks(links);
        setOpenLinksDialog(true);
      }

      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: links.length
          ? "Extension payment link generated successfully"
          : "Extension payment link sent successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });
      if (onRefreshData) {
        onRefreshData();
      } else {
        refresh();
      }
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to send extension payment link",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setSubmittingRequestId(null);
    }
  };

  return (
    <>
      <TableWrapper>
        <TableTopHeader>
          <Title>{title}</Title>
        </TableTopHeader>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            overflowX: "auto",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <StyledTable size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "rgba(232, 235, 237, 0.3) !important",
                }}
              >
                <TableHeader>ID</TableHeader>
                <TableHeader>Coupon Order ID</TableHeader>
                <TableHeader>Extend By</TableHeader>
                <TableHeader>Amount</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created By</TableHeader>
                <TableHeader>Razorpay Order ID</TableHeader>
                <TableHeader>Created At</TableHeader>
                <TableHeader>Updated At</TableHeader>
                <TableHeader align="right">Action</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && extensionRequests.length ? (
                extensionRequests.map((request) => {
                  const normalizedStatus = (
                    request?.status || ""
                  ).toUpperCase();
                  const isRowLoading = submittingRequestId === request.id;
                  const isPaid = normalizedStatus === "PAID";
                  const createdByValue = request?.createdBy;
                  const hasValidCreatedBy =
                    createdByValue !== null &&
                    createdByValue !== undefined &&
                    `${createdByValue}`.trim() !== "";
                  const createdByType =
                    request?.createdByType || record?.createdByType;
                  return (
                    <CustomTableRowTR key={request.id}>
                      <TableData>
                        {request?.id || "-"}
                      </TableData>
                      <TableData>
                        {request?.couponOrderId ? (
                          <Link
                            href={getCouponOrderHref(request.couponOrderId)}
                            underline="hover"
                          >
                            {request.couponOrderId}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableData>
                      <TableData>
                        {formatNullableValue(request.extendByDays)} days
                      </TableData>
                      <TableData>{formatNullableValue(request.amount)}</TableData>
                      <TableData>
                        <Chip
                          label={normalizedStatus || "-"}
                          size="small"
                          sx={{
                            ...getStatusChipStyles(normalizedStatus),
                            fontWeight: 600,
                          }}
                        />
                      </TableData>
                      <TableData>
                        {hasValidCreatedBy ? (
                          <Link
                            href={getCreatedByHref(
                              createdByValue as number | string,
                              createdByType,
                            )}
                            underline="hover"
                          >
                            {createdByValue}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableData>
                      <TableData>
                        {formatNullableValue(request.razorpayOrderId)}
                      </TableData>
                      <TableData>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {request.createdAt ? formatDateTimeWithSeconds(request.createdAt) : "-"}
                        </span>
                      </TableData>
                      <TableData>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {request.updatedAt ? formatDateTimeWithSeconds(request.updatedAt) : "-"}
                        </span>
                      </TableData>
                      <TableData align="right">
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isPaid || isRowLoading}
                          onClick={() =>
                            handleSendPaymentLink(request.id, normalizedStatus)
                          }
                          sx={{
                            width: 176,
                            height: 32,
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            px: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                            }}
                          >
                            {isRowLoading ? (
                              <>
                                <CircularProgress size={14} color="inherit" />
                                Sending...
                              </>
                            ) : isPaid ? (
                              "Paid"
                            ) : (
                              "Send Payment Link"
                            )}
                          </Box>
                        </Button>
                      </TableData>
                    </CustomTableRowTR>
                  );
                })
              ) : !isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    No coupon extension requests found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>

      <Dialog
        open={openLinksDialog}
        onClose={() => setOpenLinksDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Payment Link Generated
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {generatedLinks.map((link) => (
              <Box
                key={link}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  backgroundColor: "background.paper",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={link}
                >
                  {link}
                </Typography>
                <Tooltip title="Open link">
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(link, "_blank", "noopener,noreferrer")
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={copiedLink === link ? "Copied" : "Copy link"}>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(link)}
                  >
                    {copiedLink === link ? (
                      <DoneAllIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setOpenLinksDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ComboCouponExtensionsTab;
